import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { WindowContext, type WindowState } from "./context";
import { getDef } from "./registry";
import { useSettings } from "@/os/state/settings";
import { play } from "@/lib/sound";

export function WindowProvider({ children, initialOpen }: { children: ReactNode; initialOpen?: string }) {
    const [windows, setWindows] = useState<WindowState[]>([]);
    const [loading, setLoading] = useState<string | null>(null);
    const zRef = useRef(10);
    const lastLoadRef = useRef(0);
    const timerRef = useRef<number | null>(null);
    const constraintsRef = useRef<HTMLDivElement>(null);
    const { sliders } = useSettings();

    const openNow = useCallback((id: string) => {
        const def = getDef(id);
        if (!def) return;
        play("open");
        setWindows((prev) => {
            const existing = prev.find((w) => w.id === id);
            const z = ++zRef.current;
            if (existing) return prev.map((w) => (w.id === id ? { ...w, minimized: false, z } : w));
            const n = prev.length;
            return [...prev, { id, title: def.title, z, minimized: false, x: 210 + (n % 5) * 36, y: 48 + (n % 5) * 34 }];
        });
    }, []);

    const open = useCallback(
        (id: string) => {
            if (!getDef(id)) return;
            const lowTolerance = sliders.tolerance <= 30;
            const cooled = Date.now() - lastLoadRef.current > 45000;
            if (lowTolerance && id !== "performance" && !loading && cooled) {
                lastLoadRef.current = Date.now();
                setLoading(id);
                if (timerRef.current) window.clearTimeout(timerRef.current);
                timerRef.current = window.setTimeout(() => {
                    setLoading(null);
                    openNow(id);
                }, 2000);
                return;
            }
            openNow(id);
        },
        [sliders.tolerance, loading, openNow],
    );

    const close = useCallback((id: string) => {
        play("close");
        setWindows((prev) => prev.filter((w) => w.id !== id));
    }, []);

    const focus = useCallback((id: string) => {
        setWindows((prev) => {
            const w = prev.find((x) => x.id === id);
            if (!w) return prev;
            if (!w.minimized && w.z === zRef.current) return prev;
            const z = ++zRef.current;
            return prev.map((x) => (x.id === id ? { ...x, minimized: false, z } : x));
        });
    }, []);

    const minimize = useCallback((id: string) => {
        play("click");
        setWindows((prev) => prev.map((w) => (w.id === id ? { ...w, minimized: true } : w)));
    }, []);

    const skipLoading = useCallback(() => {
        if (timerRef.current) window.clearTimeout(timerRef.current);
        setLoading(null);
    }, []);

    useEffect(() => {
        if (initialOpen) openNow(initialOpen);
    }, [initialOpen, openNow]);

    useEffect(() => {
        return () => {
            if (timerRef.current) window.clearTimeout(timerRef.current);
        };
    }, []);

    const focusedId = useMemo(() => {
        const vis = windows.filter((w) => !w.minimized);
        if (!vis.length) return null;
        return vis.reduce((a, b) => (a.z > b.z ? a : b)).id;
    }, [windows]);

    const api = useMemo(
        () => ({ windows, focusedId, loading, constraintsRef, open, close, focus, minimize, skipLoading }),
        [windows, focusedId, loading, open, close, focus, minimize, skipLoading],
    );

    return <WindowContext.Provider value={api}>{children}</WindowContext.Provider>;
}
